import { useState } from "react";
import { format } from "date-fns";
import { Loader2, Pencil, Pin, PinOff, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { OnboardingAnnouncement } from "@forgepoint/shared";
import { MarkdownReact } from "@/components/MarkdownReact";
import { useOnboardingStore } from "@/modules/onboarding/onboardingStore";

interface AnnouncementEditorRowProps {
  announcement: OnboardingAnnouncement;
  authorName: string | undefined;
}

/**
 * One posted announcement with pin, edit (markdown textarea), and delete.
 */
export function AnnouncementEditorRow({
  announcement,
  authorName,
}: AnnouncementEditorRowProps) {
  const editState = useOnboardingStore((state) =>
    state.announcementStateById.get(announcement.id),
  );
  const updateAnnouncement = useOnboardingStore(
    (state) => state.updateAnnouncement,
  );
  const deleteAnnouncement = useOnboardingStore(
    (state) => state.deleteAnnouncement,
  );

  const [isEditing, setIsEditing] = useState(false);
  const [body, setBody] = useState(announcement.body);

  const handleSave = async () => {
    const trimmed = body.trim();
    if (!trimmed) {
      return;
    }
    const saved = await updateAnnouncement(announcement.id, { body: trimmed });
    if (saved) {
      setIsEditing(false);
    }
  };

  return (
    <div className="rounded-md border p-3">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {announcement.pinned && <Pin className="h-3 w-3" />}
        <span className="font-medium text-foreground">
          {authorName ?? "Forgepoint"}
        </span>
        {announcement.isForgepointAuthor && (
          <Badge variant="outline" className="border-primary/40 text-primary">
            Forgepoint
          </Badge>
        )}
        <span>{format(announcement.createdAt, "MMM d, yyyy")}</span>
        <div className="ml-auto flex items-center gap-1">
          {editState?.isSaving && (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          )}
          {editState?.error && (
            <span className="text-destructive">{editState.error}</span>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() =>
              updateAnnouncement(announcement.id, {
                pinned: !announcement.pinned,
              })
            }
          >
            {announcement.pinned ? (
              <PinOff className="h-3.5 w-3.5" />
            ) : (
              <Pin className="h-3.5 w-3.5" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => {
              setBody(announcement.body);
              setIsEditing(!isEditing);
            }}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-destructive"
            onClick={() => deleteAnnouncement(announcement.id)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      {isEditing ? (
        <div className="mt-2 space-y-2">
          <Textarea
            value={body}
            onChange={(event) => setBody(event.target.value)}
            rows={3}
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave}>
              Save
              {editState?.isSaving && (
                <Loader2 className="ml-2 h-3.5 w-3.5 animate-spin" />
              )}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setIsEditing(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="prose prose-sm mt-2 max-w-none dark:prose-invert">
          <MarkdownReact>{announcement.body}</MarkdownReact>
        </div>
      )}
    </div>
  );
}
